import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import {
  AuthCard,
  AuthCardPage,
  AuthField,
  PasswordInput,
  PasswordStrength,
  authPrimaryButton,
} from "@/components/auth/AuthLayout";

/**
 * Change your own password. Users whose password was set by someone else (new account, admin reset,
 * recovery) are sent here and cannot use the CRM until they have chosen their own.
 */
export function ChangePasswordPage() {
  const { user, refreshUser, logout } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const required = !!user?.must_change_password;
  const isSuperAdmin = String(user?.UserRole?.RoleCode || "").toUpperCase() === "SUPER_ADMIN";
  const minLength = isSuperAdmin ? 12 : 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < minLength) {
      toastError("Password too short", `Your new password must be at least ${minLength} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      toastError("Passwords do not match", "Type the same new password in both fields.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/api/User/ChangePassword", { old_password: current, password });
      await refreshUser();
      toastSuccess("Password changed", "You were signed out on your other devices.");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toastError("Could not change password", err.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const matches = confirmPassword.length > 0 && password === confirmPassword;

  const back = required ? (
    <button
      type="button"
      onClick={async () => {
        await logout();
        navigate("/login", { replace: true });
      }}
      className="text-sm font-medium text-primary hover:underline"
    >
      Log out
    </button>
  ) : (
    <Link to="/dashboard" className="text-sm font-medium text-primary hover:underline">
      Back to dashboard
    </Link>
  );

  return (
    <AuthCardPage topRight={back}>
      <form onSubmit={handleSubmit} className="w-full flex justify-center">
        <AuthCard
          title={required ? "Choose your own password" : "Change password"}
          subtitle={
            required
              ? "Your current password was set for you. Choose a new one that only you know before continuing."
              : "Changing your password signs you out on every other device."
          }
        >
          <AuthField id="current" label={required ? "Temporary password" : "Current password"} icon={<Lock />}>
            <PasswordInput id="current" value={current} onChange={setCurrent} autoComplete="current-password" autoFocus />
          </AuthField>
          <AuthField id="password" label="New password" icon={<Lock />}>
            <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" />
          </AuthField>
          <PasswordStrength password={password} minLength={minLength} />
          <AuthField
            id="confirmPassword"
            label="Type it again"
            icon={<Lock />}
            tone={confirmPassword ? (matches ? "ok" : "bad") : undefined}
            right={
              confirmPassword ? (
                <span className={`pr-2 text-xs font-semibold ${matches ? "text-success" : "text-danger"}`}>
                  {matches ? "Matches" : "Doesn’t match"}
                </span>
              ) : null
            }
          >
            <PasswordInput id="confirmPassword" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
          </AuthField>
          <p className="flex gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="size-4 shrink-0 text-primary" />
            Common or previously leaked passwords, and your name or email, are not accepted.
          </p>
          <button type="submit" disabled={submitting} className={authPrimaryButton}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : "Save new password"}
          </button>
        </AuthCard>
      </form>
    </AuthCardPage>
  );
}
